import React, { useEffect, useState } from "react";
import axios from "axios";
const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

const API = `${ENV_API_BASE_URL}/api/ayurveda-page`;
// Empty templates for dynamic items
const EMPTY_CONNECTION_ITEM = { heading: "", icon: "", paragraph: "" };
const EMPTY_BENEFIT_ITEM = { heading: "", icon: "", listParagraph: "" };
const EMPTY_DINACHARYA_ITEM = { heading: "", paragraph: "", icon: "" };
const EMPTY_THERAPY_ITEM = { icon: "", heading: "", desc: "" };

const EMPTY_FORM = {
  id: "",
  // Hero
  heroTitle: "Ayurveda",
  heroSubtitle: "The Science of Life & Longevity",
  heroImage: null,
  // What is Ayurveda?
  whatIsAyurvedaHeading: "What is Ayurveda?",
  whatIsAyurvedaParagraph: "",
  whatIsAyurvedaImage: null,
  // Vata (fixed)
  vataIcon: "",
  vataHeading: "Vata (Air & Space)",
  vataDescription: "",
  vataTips: "",
  // Pitta (fixed)
  pittaIcon: "",
  pittaHeading: "Pitta (Fire & Water)",
  pittaDescription: "",
  pittaTips: "",
  // Kapha (fixed)
  kaphaIcon: "",
  kaphaHeading: "Kapha (Earth & Water)",
  kaphaDescription: "",
  kaphaTips: "",
  // Teacher Training (fixed)
  teacherTrainingHeading: "",
  teacherTrainingDesc: "",
  teacherTrainingImage: null,
  // Ritucharya (fixed)
  seasonal: "",
  ritucharyaWinter: "",
  ritucharyaWinterParagraph: "",
  ritucharyaSummer: "",
  ritucharyaSummerParagraph: "",
  ritucharyaMonsoon: "",
  ritucharyaMonsoonParagraph: "",
  // Food (fixed)
  Food: "",
  FoodImage: null,
  foodPara: "",
  foodPrinciplesHeading: "",
  foodPrinciplesPara: "",
  // Benefits (fixed top-level fields, but benefitsItems is dynamic)
  benefitsList: "",
  benefitsPara: "",
  benefitsImage: null,
  // Connection (fixed top-level fields, but connectionItems is dynamic)
  Connection: "",
  ConnectionPara: "",
  // Dynamic arrays
  connectionItems: [],
  benefitsItems: [],
  dinacharyaItems: [],
  therapyItems: [],
};

function AyurvedaForm() {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [tableData, setTableData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [imagePreviews, setImagePreviews] = useState({});

  // ---------- Fetch Data ----------
  const fetchData = async () => {
    try {
      const res = await axios.get(API);
      let data = res.data?.data || [];
      if (!Array.isArray(data)) {
        data = [data];
      }
      // Transform: map _id to id, parse array fields
      data = data.map(item => {
        const parsedItem = {
          ...item,
          id: item._id, // crucial for frontend
        };
        ['connectionItems', 'benefitsItems', 'dinacharyaItems', 'therapyItems'].forEach(field => {
          if (parsedItem[field]) {
            if (typeof parsedItem[field] === 'string') {
              try {
                parsedItem[field] = JSON.parse(parsedItem[field]);
              } catch (e) {
                parsedItem[field] = [];
              }
            } else if (!Array.isArray(parsedItem[field])) {
              parsedItem[field] = [];
            }
          } else {
            parsedItem[field] = [];
          }
        });
        return parsedItem;
      });
      setTableData(data);
    } catch (error) {
      console.error("Failed to load Ayurveda content:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Cleanup previews
  useEffect(() => {
    return () => {
      Object.values(imagePreviews).forEach(url => URL.revokeObjectURL(url));
    };
  }, [imagePreviews]);

  // ---------- Handlers for top-level text inputs ----------
  const handleTextChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // File change for top-level file fields
  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      setFormData(prev => ({ ...prev, [name]: file }));
      const previewUrl = URL.createObjectURL(file);
      setImagePreviews(prev => ({ ...prev, [name]: previewUrl }));
    }
  };

  // ---------- Dynamic array helpers ----------
  const addNestedItem = (arrayName, emptyItem) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: [...prev[arrayName], emptyItem]
    }));
  };

  const updateNestedItem = (arrayName, index, field, value) => {
    setFormData(prev => {
      const newArray = [...prev[arrayName]];
      newArray[index] = { ...newArray[index], [field]: value };
      return { ...prev, [arrayName]: newArray };
    });
  };

  const removeNestedItem = (arrayName, index) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== index)
    }));
  };

  // ---------- Submit (Create / Update) ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const data = new FormData();
    Object.keys(formData).forEach(key => {
      if (key === "id" && editId) {
        data.append("id", editId);
      } else if (Array.isArray(formData[key])) {
        // Stringify arrays for backend
        data.append(key, JSON.stringify(formData[key]));
      } else if (formData[key] instanceof File) {
        data.append(key, formData[key]);
      } else if (formData[key] !== null && formData[key] !== undefined) {
        data.append(key, formData[key]);
      }
    });

    try {
      if (editId) {
        await axios.put(`${API}/${editId}`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await axios.post(API, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      fetchData();               // refresh table
      setShowForm(false);        // go back to table view
      setFormData(EMPTY_FORM);   // reset form
      setEditId(null);
      setImagePreviews({});
    } catch (error) {
      console.error("Save failed:", error);
    }
    setSaving(false);
  };

  // ---------- Edit (populate form with existing data) ----------
  const handleEdit = (item) => {
    // item already has parsed arrays and an id (from fetchData)
    setFormData({
      ...EMPTY_FORM,
      ...item,
      // Reset file fields – user must select new files to change them
      heroImage: null,
      whatIsAyurvedaImage: null,
      teacherTrainingImage: null,
      FoodImage: null,
      benefitsImage: null,
    });
    setEditId(item.id);
    setShowForm(true);
    setImagePreviews({});
  };

  // ---------- Delete ----------
 const handleDelete = async (id) => {
  if (!window.confirm("Are you sure?")) return;
  console.log("Attempting to delete", id);
  try {
    const response = await axios.delete(`${API}/${id}`);
    console.log("Delete response", response);
    fetchData();
    alert("Deleted successfully");
  } catch (error) {
    console.error("Delete failed:", error);
    alert("Delete failed: " + error.message);
  }
};

  return (
    <div className="container pt-5 mt-5">
      {/* ========== TABLE VIEW ========== */}
      {!showForm && (
        <>
          <div className="d-flex justify-content-between mb-3">
            <h4>Ayurveda Page Content</h4>
            <button
              className="btn btn-primary"
              onClick={() => {
                setShowForm(true);
                setFormData(EMPTY_FORM);
                setEditId(null);
                setImagePreviews({});
              }}
            >
              Create New
            </button>
          </div>

          <div className="table-responsive">
            <table className="table table-bordered table-striped">
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>Hero Title</th>
                  <th>What is Ayurveda?</th>
                  <th>Vata</th>
                  <th>Pitta</th>
                  <th>Kapha</th>
                  <th>Therapies (count)</th>
                  <th>Teacher Training</th>
                  <th>Dinacharya (count)</th>
                  <th>Ritucharya</th>
                  <th>Food</th>
                  <th>Benefits (count)</th>
                  <th>Connection (count)</th>
                  <th width="180">Action</th>
                </tr>
              </thead>
              <tbody>
                {tableData.length === 0 ? (
                  <tr>
                    <td colSpan="14" className="text-center">No Data Found</td>
                  </tr>
                ) : (
                  tableData.map((item, index) => (
                    <tr key={item.id}>
                      <td>{index + 1}</td>
                      <td>{item.heroTitle}</td>
                      <td>{item.whatIsAyurvedaParagraph?.substring(0, 30)}...</td>
                      <td>{item.vataDescription?.substring(0, 30)}...</td>
                      <td>{item.pittaDescription?.substring(0, 30)}...</td>
                      <td>{item.kaphaDescription?.substring(0, 30)}...</td>
                      <td>{Array.isArray(item.therapyItems) ? item.therapyItems.length : 0}</td>
                      <td>{item.teacherTrainingDesc?.substring(0, 30)}...</td>
                      <td>{Array.isArray(item.dinacharyaItems) ? item.dinacharyaItems.length : 0}</td>
                      <td>{item.ritucharyaWinter?.substring(0, 20)}...</td>
                      <td>{item.foodPara?.substring(0, 30)}...</td>
                      <td>{Array.isArray(item.benefitsItems) ? item.benefitsItems.length : 0}</td>
                      <td>{Array.isArray(item.connectionItems) ? item.connectionItems.length : 0}</td>
                      <td>
                        <button
                          className="btn btn-warning btn-sm me-2"
                          onClick={() => handleEdit(item)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(item.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ========== FORM VIEW ========== */}
      {showForm && (
        <form onSubmit={handleSubmit} className="card p-4" encType="multipart/form-data">
          <h4 className="mb-3">
            {editId ? "Edit Ayurveda Content" : "Create Ayurveda Content"}
          </h4>

          {/* Hero Section */}
          <h5 className="mt-3 mb-2">Hero Section</h5>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Hero Title</label>
              <input
                type="text"
                className="form-control"
                name="heroTitle"
                value={formData.heroTitle}
                onChange={handleTextChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Hero Subtitle</label>
              <input
                type="text"
                className="form-control"
                name="heroSubtitle"
                value={formData.heroSubtitle}
                onChange={handleTextChange}
              />
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label fw-semibold">Hero Background Image</label>
            <input
              type="file"
              className="form-control"
              name="heroImage"
              accept="image/*"
              onChange={handleFileChange}
            />
            {imagePreviews.heroImage && (
              <img src={imagePreviews.heroImage} alt="Preview" style={{ maxWidth: "200px", marginTop: "10px" }} />
            )}
          </div>

          {/* What is Ayurveda? */}
          <h5 className="mt-4 mb-2">What is Ayurveda?</h5>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Heading</label>
              <input
                type="text"
                className="form-control"
                name="whatIsAyurvedaHeading"
                value={formData.whatIsAyurvedaHeading}
                onChange={handleTextChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Image</label>
              <input
                type="file"
                className="form-control"
                name="whatIsAyurvedaImage"
                accept="image/*"
                onChange={handleFileChange}
              />
              {imagePreviews.whatIsAyurvedaImage && (
                <img src={imagePreviews.whatIsAyurvedaImage} alt="Preview" style={{ maxWidth: "200px", marginTop: "10px" }} />
              )}
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label fw-semibold">Paragraph</label>
            <textarea
              className="form-control"
              rows="3"
              name="whatIsAyurvedaParagraph"
              value={formData.whatIsAyurvedaParagraph}
              onChange={handleTextChange}
            />
          </div>

          {/* Doshas */}
          <h5 className="mt-4 mb-2">The Three Doshas</h5>
          <div className="row">
            {/* Vata */}
            <div className="col-md-4 border p-3">
              <h6>Vata (Air & Space)</h6>
              <div className="mb-2">
                <label className="form-label fw-semibold">Icon URL</label>
                <input
                  type="text"
                  className="form-control"
                  name="vataIcon"
                  value={formData.vataIcon}
                  onChange={handleTextChange}
                />
              </div>
              <div className="mb-2">
                <label className="form-label fw-semibold">Heading</label>
                <input
                  type="text"
                  className="form-control"
                  name="vataHeading"
                  value={formData.vataHeading}
                  onChange={handleTextChange}
                />
              </div>
              <div className="mb-2">
                <label className="form-label fw-semibold">Description</label>
                <textarea
                  className="form-control"
                  rows="8"
                  name="vataDescription"
                  value={formData.vataDescription}
                  onChange={handleTextChange}
                />
              </div>
              <div className="mb-2">
                <label className="form-label fw-semibold">Balance Tips</label>
                <textarea
                  className="form-control"
                  rows="2"
                  name="vataTips"
                  value={formData.vataTips}
                  onChange={handleTextChange}
                />
              </div>
            </div>
            {/* Pitta */}
            <div className="col-md-4 border p-3">
              <h6>Pitta (Fire & Water)</h6>
              <div className="mb-2">
                <label className="form-label fw-semibold">Icon URL</label>
                <input
                  type="text"
                  className="form-control"
                  name="pittaIcon"
                  value={formData.pittaIcon}
                  onChange={handleTextChange}
                />
              </div>
              <div className="mb-2">
                <label className="form-label fw-semibold">Heading</label>
                <input
                  type="text"
                  className="form-control"
                  name="pittaHeading"
                  value={formData.pittaHeading}
                  onChange={handleTextChange}
                />
              </div>
              <div className="mb-2">
                <label className="form-label fw-semibold">Description</label>
                <textarea
                  className="form-control"
                  rows="2"
                  name="pittaDescription"
                  value={formData.pittaDescription}
                  onChange={handleTextChange}
                />
              </div>
              <div className="mb-2">
                <label className="form-label fw-semibold">Balance Tips</label>
                <textarea
                  className="form-control"
                  rows="2"
                  name="pittaTips"
                  value={formData.pittaTips}
                  onChange={handleTextChange}
                />
              </div>
            </div>
            {/* Kapha */}
            <div className="col-md-4 border p-3">
              <h6>Kapha (Earth & Water)</h6>
              <div className="mb-2">
                <label className="form-label fw-semibold">Icon URL</label>
                <input
                  type="text"
                  className="form-control"
                  name="kaphaIcon"
                  value={formData.kaphaIcon}
                  onChange={handleTextChange}
                />
              </div>
              <div className="mb-2">
                <label className="form-label fw-semibold">Heading</label>
                <input
                  type="text"
                  className="form-control"
                  name="kaphaHeading"
                  value={formData.kaphaHeading}
                  onChange={handleTextChange}
                />
              </div>
              <div className="mb-2">
                <label className="form-label fw-semibold">Description</label>
                <textarea
                  className="form-control"
                  rows="2"
                  name="kaphaDescription"
                  value={formData.kaphaDescription}
                  onChange={handleTextChange}
                />
              </div>
              <div className="mb-2">
                <label className="form-label fw-semibold">Balance Tips</label>
                <textarea
                  className="form-control"
                  rows="2"
                  name="kaphaTips"
                  value={formData.kaphaTips}
                  onChange={handleTextChange}
                />
              </div>
            </div>
          </div>

          {/* Therapies (dynamic cards) */}
          <h5 className="mt-4 mb-2">Ayurvedic Therapies</h5>
          {formData.therapyItems.map((item, index) => (
            <div key={index} className="border rounded p-3 mb-3">
              <label className="form-label fw-semibold">Icon URL</label>
              <input
                type="text"
                className="form-control mb-2"
                value={item.icon}
                onChange={(e) => updateNestedItem("therapyItems", index, "icon", e.target.value)}
              />
              <label className="form-label fw-semibold">Heading</label>
              <input
                type="text"
                className="form-control mb-2"
                value={item.heading}
                onChange={(e) => updateNestedItem("therapyItems", index, "heading", e.target.value)}
              />
              <label className="form-label fw-semibold">Description</label>
              <textarea
                className="form-control"
                rows={2}
                value={item.desc}
                onChange={(e) => updateNestedItem("therapyItems", index, "desc", e.target.value)}
              />
              <button
                type="button"
                className="btn btn-outline-danger mt-2"
                onClick={() => removeNestedItem("therapyItems", index)}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-outline-primary mb-4"
            onClick={() => addNestedItem("therapyItems", EMPTY_THERAPY_ITEM)}
          >
            + Add Therapy
          </button>

          {/* Teacher Training */}
          <h5 className="mt-4 mb-2">Ayurveda in Yoga Teacher Training</h5>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Heading</label>
              <input
                type="text"
                className="form-control"
                name="teacherTrainingHeading"
                value={formData.teacherTrainingHeading}
                onChange={handleTextChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Image</label>
              <input
                type="file"
                className="form-control"
                name="teacherTrainingImage"
                accept="image/*"
                onChange={handleFileChange}
              />
              {imagePreviews.teacherTrainingImage && (
                <img src={imagePreviews.teacherTrainingImage} alt="Preview" style={{ maxWidth: "200px", marginTop: "10px" }} />
              )}
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label fw-semibold">Description</label>
            <textarea
              className="form-control"
              rows="4"
              name="teacherTrainingDesc"
              value={formData.teacherTrainingDesc}
              onChange={handleTextChange}
            />
          </div>

          {/* Dinacharya (dynamic cards) */}
          <h5 className="mt-4 mb-2">Dinacharya (Daily Routines)</h5>
          {formData.dinacharyaItems.map((item, index) => (
            <div key={index} className="border rounded p-3 mb-3">
              <label className="form-label fw-semibold">Heading</label>
              <input
                type="text"
                className="form-control mb-2"
                value={item.heading}
                onChange={(e) => updateNestedItem("dinacharyaItems", index, "heading", e.target.value)}
              />
              <label className="form-label fw-semibold">Paragraph</label>
              <textarea
                className="form-control mb-2"
                rows={2}
                value={item.paragraph}
                onChange={(e) => updateNestedItem("dinacharyaItems", index, "paragraph", e.target.value)}
              />
              <label className="form-label fw-semibold">Icon URL</label>
              <input
                type="text"
                className="form-control mb-2"
                value={item.icon}
                onChange={(e) => updateNestedItem("dinacharyaItems", index, "icon", e.target.value)}
              />
              <button
                type="button"
                className="btn btn-outline-danger mt-2"
                onClick={() => removeNestedItem("dinacharyaItems", index)}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-outline-primary mb-4"
            onClick={() => addNestedItem("dinacharyaItems", EMPTY_DINACHARYA_ITEM)}
          >
            + Add Dinacharya Item
          </button>

          {/* Ritucharya (fixed) */}
          <h5 className="mt-4 mb-2">Ritucharya (Seasonal Wisdom)</h5>
          <div className="mb-3">
            <label className="form-label fw-semibold">Seasonal Heading</label>
            <input
              type="text"
              className="form-control"
              name="seasonal"
              value={formData.seasonal}
              onChange={handleTextChange}
            />
          </div>
          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label fw-semibold">Winter Title</label>
              <input
                type="text"
                className="form-control"
                name="ritucharyaWinter"
                value={formData.ritucharyaWinter}
                onChange={handleTextChange}
              />
            </div>
            <div className="col-md-8 mb-3">
              <label className="form-label fw-semibold">Winter Paragraph</label>
              <textarea
                className="form-control"
                rows="2"
                name="ritucharyaWinterParagraph"
                value={formData.ritucharyaWinterParagraph}
                onChange={handleTextChange}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label fw-semibold">Summer Title</label>
              <input
                type="text"
                className="form-control"
                name="ritucharyaSummer"
                value={formData.ritucharyaSummer}
                onChange={handleTextChange}
              />
            </div>
            <div className="col-md-8 mb-3">
              <label className="form-label fw-semibold">Summer Paragraph</label>
              <textarea
                className="form-control"
                rows="2"
                name="ritucharyaSummerParagraph"
                value={formData.ritucharyaSummerParagraph}
                onChange={handleTextChange}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label fw-semibold">Monsoon Title</label>
              <input
                type="text"
                className="form-control"
                name="ritucharyaMonsoon"
                value={formData.ritucharyaMonsoon}
                onChange={handleTextChange}
              />
            </div>
            <div className="col-md-8 mb-3">
              <label className="form-label fw-semibold">Monsoon Paragraph</label>
              <textarea
                className="form-control"
                rows="2"
                name="ritucharyaMonsoonParagraph"
                value={formData.ritucharyaMonsoonParagraph}
                onChange={handleTextChange}
              />
            </div>
          </div>

          {/* Food (fixed) */}
          <h5 className="mt-4 mb-2">Food & Nutrition</h5>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Food Heading</label>
              <input
                type="text"
                className="form-control"
                name="Food"
                value={formData.Food}
                onChange={handleTextChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Food Image</label>
              <input
                type="file"
                className="form-control"
                name="FoodImage"
                accept="image/*"
                onChange={handleFileChange}
              />
              {imagePreviews.FoodImage && (
                <img src={imagePreviews.FoodImage} alt="Preview" style={{ maxWidth: "200px", marginTop: "10px" }} />
              )}
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label fw-semibold">Food Paragraph</label>
            <textarea
              className="form-control"
              rows="3"
              name="foodPara"
              value={formData.foodPara}
              onChange={handleTextChange}
            />
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Food Principles Heading</label>
              <input
                type="text"
                className="form-control"
                name="foodPrinciplesHeading"
                value={formData.foodPrinciplesHeading}
                onChange={handleTextChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Food Principles Paragraph</label>
              <textarea
                className="form-control"
                rows="2"
                name="foodPrinciplesPara"
                value={formData.foodPrinciplesPara}
                onChange={handleTextChange}
              />
            </div>
          </div>

          {/* Benefits (dynamic cards) */}
          <h5 className="mt-4 mb-2">Benefits of Ayurveda</h5>
          {/* Fixed top-level fields for Benefits */}
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Benefits List (overview)</label>
              <textarea
                className="form-control"
                rows="2"
                name="benefitsList"
                value={formData.benefitsList}
                onChange={handleTextChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Benefits Paragraph</label>
              <textarea
                className="form-control"
                rows="2"
                name="benefitsPara"
                value={formData.benefitsPara}
                onChange={handleTextChange}
              />
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label fw-semibold">Benefits Image</label>
            <input
              type="file"
              className="form-control"
              name="benefitsImage"
              accept="image/*"
              onChange={handleFileChange}
            />
            {imagePreviews.benefitsImage && (
              <img src={imagePreviews.benefitsImage} alt="Preview" style={{ maxWidth: "200px", marginTop: "10px" }} />
            )}
          </div>

          <h6 className="mt-3">Benefit Items (dynamic)</h6>
          {formData.benefitsItems.map((item, index) => (
            <div key={index} className="border rounded p-3 mb-3">
              <label className="form-label fw-semibold">Heading</label>
              <input
                type="text"
                className="form-control mb-2"
                value={item.heading}
                onChange={(e) => updateNestedItem("benefitsItems", index, "heading", e.target.value)}
              />
              <label className="form-label fw-semibold">Icon URL</label>
              <input
                type="text"
                className="form-control mb-2"
                value={item.icon}
                onChange={(e) => updateNestedItem("benefitsItems", index, "icon", e.target.value)}
              />
              <label className="form-label fw-semibold">List Paragraph</label>
              <textarea
                className="form-control"
                rows={2}
                value={item.listParagraph}
                onChange={(e) => updateNestedItem("benefitsItems", index, "listParagraph", e.target.value)}
              />
              <button
                type="button"
                className="btn btn-outline-danger mt-2"
                onClick={() => removeNestedItem("benefitsItems", index)}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-outline-primary mb-4"
            onClick={() => addNestedItem("benefitsItems", EMPTY_BENEFIT_ITEM)}
          >
            + Add Benefit Item
          </button>

          {/* Connection (dynamic cards) */}
          <h5 className="mt-4 mb-2">Connection</h5>
          {/* Fixed top-level fields for Connection */}
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Connection (short)</label>
              <input
                type="text"
                className="form-control"
                name="Connection"
                value={formData.Connection}
                onChange={handleTextChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Connection Paragraph (main)</label>
              <textarea
                className="form-control"
                rows="2"
                name="ConnectionPara"
                value={formData.ConnectionPara}
                onChange={handleTextChange}
              />
            </div>
          </div>

          <h6 className="mt-3">Connection Items (dynamic)</h6>
          {formData.connectionItems.map((item, index) => (
            <div key={index} className="border rounded p-3 mb-3">
              <label className="form-label fw-semibold">Heading</label>
              <input
                type="text"
                className="form-control mb-2"
                value={item.heading}
                onChange={(e) => updateNestedItem("connectionItems", index, "heading", e.target.value)}
              />
              <label className="form-label fw-semibold">Icon URL</label>
              <input
                type="text"
                className="form-control mb-2"
                value={item.icon}
                onChange={(e) => updateNestedItem("connectionItems", index, "icon", e.target.value)}
              />
              <label className="form-label fw-semibold">Paragraph</label>
              <textarea
                className="form-control"
                rows={2}
                value={item.paragraph}
                onChange={(e) => updateNestedItem("connectionItems", index, "paragraph", e.target.value)}
              />
              <button
                type="button"
                className="btn btn-outline-danger mt-2"
                onClick={() => removeNestedItem("connectionItems", index)}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-outline-primary mb-4"
            onClick={() => addNestedItem("connectionItems", EMPTY_CONNECTION_ITEM)}
          >
            + Add Connection Item
          </button>

          {/* Form Buttons */}
          <div className="d-flex gap-2">
            <button className="btn btn-success" type="submit" disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setShowForm(false);
                setFormData(EMPTY_FORM);
                setEditId(null);
                setImagePreviews({});
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default AyurvedaForm;